<script>
import { mapGetters, mapActions } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'IndexingTool',
  components: { NextButton },
  props: {
    knowledgebaseId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      interval: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      records: 'records/getRecords',
      pagination: 'records/getPagination',
      uiFlags: 'records/getUIFlags',
    }),
  },
  methods: {
    ...mapActions({
      getRecords: 'records/get',
      clearRecords: 'records/clearRecords',
      loadMore: 'records/loadMore',
      reindexRecord: 'records/reindex',
      reindexAllRecords: 'records/reindexAll',
      refreshLoadedRecordsAction: 'records/refreshLoaded',
    }),
    async fetchRecords() {
      if (this.isLoading) return;

      this.isLoading = true;
      try {
        await this.getRecords(this.knowledgebaseId);
      } finally {
        this.isLoading = false;
      }
    },
    async refreshLoadedRecords() {
      if (this.isLoading) return;
      if (this.uiFlags?.isLoadingMore) return;

      this.isLoading = true;
      try {
        await this.refreshLoadedRecordsAction(this.knowledgebaseId);
      } finally {
        this.isLoading = false;
      }
    },
    async loadMoreRecords() {
      await this.loadMore(this.knowledgebaseId);
    },
    async handleReindex(record) {
      await this.reindexRecord({
        knowledgebaseId: this.knowledgebaseId,
        recordId: record.id,
      });
      this.refreshLoadedRecords();
    },
    async handleReindexAll() {
      await this.reindexAllRecords({ knowledgebaseId: this.knowledgebaseId });
      this.refreshLoadedRecords();
    },
  },
  async activated() {
    await this.fetchRecords();
  },
  async deactivated() {
    await this.clearRecords();
  },
  async mounted() {
    this.interval = window.setInterval(() => {
      this.refreshLoadedRecords();
    }, 10000);
  },
  async unmounted() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },
};
</script>

<template>
  <div class="flex-1 h-full flex flex-col pt-6 !overflow-auto">
    <div
      v-if="isLoading && records.length === 0"
      class="text-neutral-500 py-4 px-4"
    >
      {{ $t('LIBRARIES.TOOLS.LOADING') }}
    </div>
    <table v-else class="w-full woot-table mb-6">
      <thead class="border-b border-neutral-100">
        <tr>
          <th>
            {{ $t('LIBRARIES.RECORDS.ID.LABEL') }}
          </th>
          <th>
            {{ $t('LIBRARIES.RECORDS.FILE_NAME.LABEL') }}
          </th>
          <th>
            <span class="whitespace-nowrap">
              {{ $t('LIBRARIES.RECORDS.FILE_TYPE.LABEL') }}
            </span>
          </th>
          <th>
            {{ $t('LIBRARIES.RECORDS.UPDATED_AT.LABEL') }}
          </th>
          <th>
            {{ $t('LIBRARIES.RECORDS.STATUS.LABEL') }}
          </th>
          <th class="w-12" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="record in records"
          :key="record.id"
          class="border-b border-neutral-100"
        >
          <td>
            {{ record.id }}
          </td>
          <td>
            <span class="inline-block max-w-full break-all">
              {{ record.file ? record.file.filename : record.source }}
            </span>
          </td>
          <td class="uppercase">
            {{ record.file ? record.file.extension : 'URL' }}
          </td>
          <td>
            {{ new Date(record.updated_at).toLocaleString() }}
          </td>
          <td>
            {{ record.status }}
          </td>
          <td>
            <NextButton
              v-tooltip.top-end="$t('LIBRARIES.RECORDS.REINDEX.LABEL')"
              icon="i-lucide-refresh-ccw-dot"
              md
              slate
              @click="handleReindex(record)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="pagination?.hasMore" class="flex justify-center py-2">
      <NextButton
        :label="$t('LIBRARIES.RECORDS_TOOL.LOAD_MORE')"
        md
        slate
        :is-loading="uiFlags.isLoadingMore"
        @click="loadMoreRecords"
      />
    </div>

    <div class="flex flex-row justify-end items-center gap-2 py-2 px-0 w-full">
      <span class="text-sm text-neutral-500">
        {{
          $t('LIBRARIES.RECORDS_TOOL.COUNT', { count: pagination.totalCount })
        }}
      </span>
      <NextButton
        icon="i-lucide-refresh-ccw-dot"
        :label="$t('LIBRARIES.RECORDS.REINDEX_ALL.LABEL')"
        md
        @click="handleReindexAll()"
      />
    </div>
  </div>
</template>
